import { AutonomousIntelligence } from '@/services/intelligence/AutonomousIntelligence';
import { MarketingService } from '@/services/marketing/MarketingService';
import { CampaignStatus, type ScheduledPost } from '@/modules/marketing/types';
import { audioIntelligence } from '@/services/audio/AudioIntelligenceService';
// useStore removed

import { z } from 'zod';
import { zodToJsonSchema } from 'zod-to-json-schema';
// duplicate removed
import { wrapTool, toolSuccess, toolError } from '../utils/ToolUtils';
import type { AnyToolFunction } from '../types';
import { logger } from '@/utils/logger';
import { importWithRetry } from '@/utils/dynamicImport';

/** Typed Electron IPC bridge for marketing tools */
interface ElectronMarketingBridge {
    analyzeTrends: (opts: { category?: string }) => Promise<{ success: boolean; error?: string; analysis?: unknown }>;
}

interface ElectronWindowAPI {
    electronAPI?: {
        marketing?: ElectronMarketingBridge;
    };
}

// --- Validation Schemas ---

const CreateCampaignBriefSchema = z.object({
    campaignName: z.string(),
    targetAudience: z.string(),
    budget: z.string(),
    channels: z.array(z.string()),
    kpis: z.array(z.string()),
    assets: z.array(z.string()).optional()
});

const AnalyzeAudienceSchema = z.object({
    platform: z.string(),
    demographics: z.object({
        age: z.string(),
        locations: z.array(z.string())
    }),
    interests: z.array(z.string()),
    reach: z.string()
});

const TrackPerformanceSchema = z.object({
    campaignId: z.string(),
    metrics: z.object({
        impressions: z.number(),
        clicks: z.number(),
        conversions: z.number()
    }),
    roi: z.string()
});

const parseCampaignBudget = (value: string): number => {
    const parsed = Number(value.replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

const parseDurationDays = (value?: string): number => {
    if (!value) return 30;
    const parsed = Number(value.match(/\d+/)?.[0]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 30;
};

const toSupportedPlatform = (channel: string): ScheduledPost['platform'] => {
    const normalized = channel.toLowerCase();
    if (normalized.includes('instagram')) return 'Instagram';
    // LinkedIn and Email are draft-only (not supported for native delivery)
    if (normalized.includes('linkedin') || normalized.includes('email') || normalized.includes('newsletter')) {
        return 'Twitter'; // Default to Twitter for draft-only channels
    }
    return 'Twitter';
};

// --- Tools Implementation ---

export const MarketingTools = {
    create_campaign_brief: wrapTool('create_campaign_brief', async ({ product, goal, budget, duration, assetIds }: { product: string; goal: string; budget?: string; duration?: string; assetIds?: string[] }) => {
        const schema = zodToJsonSchema(CreateCampaignBriefSchema);
        const prompt = `
        You are a Marketing Strategist. Create a campaign brief for: ${product}.
        Goal: ${goal}.
        ${budget ? `Budget: ${budget}` : ''}
        ${duration ? `Duration: ${duration}` : ''}
        ${assetIds ? `Attached Asset IDs: ${assetIds.join(', ')} (Incorporate these into the strategy)` : ''}
        `;

        const data = await AutonomousIntelligence.generateStructuredData<z.infer<typeof CreateCampaignBriefSchema>>(prompt, schema as Record<string, unknown>);
        const parsed = CreateCampaignBriefSchema.parse(data);

        // AUTO-PERSIST: Save the generated brief to the database
        // ISSUE-835: this used to catch-and-log a persistence failure, then
        // ALWAYS report "saved to Marketing Dashboard" regardless of whether
        // it actually was. campaignId is now only present when the write
        // really succeeded, and the response honestly reflects that.
        let campaignId: string | undefined;
        let persistErrorMessage: string | undefined;
        try {
            const durationDays = parseDurationDays(duration);
            const startDate = new Date().toISOString().split('T')[0]!;
            const channels = parsed.channels.length > 0 ? parsed.channels : ['Instagram'];
            const posts: ScheduledPost[] = channels.slice(0, 4).map((channel, index) => ({
                id: `brief-post-${index + 1}`,
                platform: toSupportedPlatform(channel),
                copy: `${parsed.campaignName}: ${goal} for ${parsed.targetAudience}.`,
                imageAsset: {
                    assetType: 'image',
                    title: `${parsed.campaignName} ${channel} creative`,
                    imageUrl: '',
                    caption: `${parsed.campaignName} campaign asset`,
                },
                day: (index * Math.max(1, Math.floor(durationDays / Math.max(channels.length, 1)))) + 1,
                status: CampaignStatus.PENDING,
            }));

            campaignId = await MarketingService.createCampaign({
                assetType: 'campaign',
                title: parsed.campaignName,
                description: `${goal} Target audience: ${parsed.targetAudience}. KPIs: ${parsed.kpis.join(', ') || 'TBD'}.`,
                durationDays,
                startDate,
                budget: parseCampaignBudget(parsed.budget),
                posts,
                status: CampaignStatus.PENDING,
                attachedAssets: assetIds || [],
            });
            logger.info(`[MarketingTools] Campaign brief persisted: ${parsed.campaignName}`);
        } catch (persistError: unknown) {
            persistErrorMessage = persistError instanceof Error ? persistError.message : String(persistError);
            logger.warn('[MarketingTools] Persistence failed:', persistError);
        }

        if (campaignId) {
            return toolSuccess(
                { ...parsed, campaignId, saved: true },
                `Campaign brief created for ${parsed.campaignName} and saved to Marketing Dashboard (ID: ${campaignId}).`
            );
        }

        return toolSuccess(
            { ...parsed, saved: false },
            `Campaign brief generated for ${parsed.campaignName}, but it was NOT saved to the Marketing Dashboard: ${persistErrorMessage}. This brief exists only in this response.`
        );
    }),

    analyze_audience: wrapTool('analyze_audience', async ({ genre, similar_artists }: { genre: string; similar_artists?: string[] }) => {
        const schema = zodToJsonSchema(AnalyzeAudienceSchema);
        const prompt = `
        You are a Market Researcher. Analyze the target audience for genre: ${genre}.
        ${similar_artists ? `Similar Artists: ${similar_artists.join(', ')}` : ''}
        `;
        const data = await AutonomousIntelligence.generateStructuredData<z.infer<typeof AnalyzeAudienceSchema>>(prompt, schema as Record<string, unknown>);
        const validated = AnalyzeAudienceSchema.parse(data);
        return toolSuccess(validated, `Audience analysis completed for ${genre}. Estimated reach: ${validated.reach}.`);
    }),

    /**
     * Enhanced Schedule Content: Uses real JS Dates instead of Autonomous hallucination for the calendar.
     */
    schedule_content: wrapTool('schedule_content', async ({ campaign_start, platforms, frequency }: { campaign_start: string; platforms: string[]; frequency: string }) => {
        const startDate = new Date(campaign_start);
        const validStartDate = isNaN(startDate.getTime()) ? new Date() : startDate;

        let intervalDays = 7;
        const freqLower = frequency.toLowerCase();
        if (freqLower.includes("daily")) intervalDays = 1;
        else if (freqLower.includes("bi-weekly") || freqLower.includes("twice a week")) intervalDays = 3;
        else if (freqLower.includes("monthly")) intervalDays = 30;

        const schedule: Array<{ date: string; platform: string; type: string }> = [];
        const postsPerPlatform = 4;

        for (let i = 0; i < postsPerPlatform; i++) {
            const postDate = new Date(validStartDate);
            postDate.setDate(validStartDate.getDate() + (i * intervalDays));

            platforms.forEach(platform => {
                schedule.push({
                    date: postDate.toISOString(),
                    platform: platform,
                    type: "Social Post"
                });
            });
        }

        schedule.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        // ISSUE-835: this schedule is generated in-memory only — nothing is
        // persisted or queued for delivery. "scheduled" implied posts would
        // actually go out; "draft_generated" is the honest state.
        return toolSuccess({
            status: "draft_generated",
            count: schedule.length,
            schedule: schedule,
            nextPost: schedule.length > 0 ? schedule[0]!.date : "None"
        }, `Draft content schedule generated with ${schedule.length} posts across ${platforms.join(', ')}. This is a plan only — no posts have been saved or queued for delivery.`);
    }),

    track_performance: wrapTool('track_performance', async ({ campaignId }: { campaignId: string }) => {
        try {
            const { auth, db } = await importWithRetry(() => import('@/services/firebase'));
            const { doc, getDoc } = await importWithRetry(() => import('firebase/firestore'));
            const uid = auth.currentUser?.uid;

            const refs = [
                ...(uid ? [doc(db, 'users', uid, 'campaign_metrics', campaignId)] : []),
                doc(db, 'campaign_metrics', campaignId)
            ];

            for (const ref of refs) {
                const snap = await getDoc(ref);
                if (!snap.exists()) continue;

                const data = snap.data();
                const validated = TrackPerformanceSchema.parse({
                    campaignId,
                    metrics: data.metrics,
                    roi: data.roi
                });
                return toolSuccess(validated, `Performance metrics loaded for Campaign ID: ${campaignId}. ROI: ${validated.roi}.`);
            }

            return toolError(`No live performance metrics found for Campaign ID: ${campaignId}.`, 'METRICS_NOT_FOUND');
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Unknown error';
            return toolError(`Failed to load performance metrics: ${msg}`, 'METRICS_READ_FAILED');
        }
    }),

    generate_campaign_from_audio: wrapTool('generate_campaign_from_audio', async ({ uploadedAudioIndex }: { uploadedAudioIndex: number }) => {
        const { useStore } = await importWithRetry(() => import('@/core/store'));
        const { uploadedAudio } = useStore.getState();
        const audioItem = uploadedAudio[uploadedAudioIndex];

        if (!audioItem) {
            return toolError("No audio found at the provided index.", "NOT_FOUND");
        }

        try {
            // 1. Analyze Audio
            const fetchRes = await fetch(audioItem.url);
            const blob = await fetchRes.blob();
            const file = new File([blob], "audio_track", { type: blob.type });
            const profile = await audioIntelligence.analyze(file);

            // 2. Synthesize Campaign using Semantic Data
            const { mood, genre, marketingHooks } = profile.semantic;

            // 3. Delegate to create_campaign_brief
            // We re-use logic by calling the exported function or just returning data for the agent to act on
            // Better to return the insight so the agent handles the creation step via reasoning

            return toolSuccess({
                insight: `Analyzed track. Genre: ${genre.join(', ')}. Mood: ${mood.join(', ')}.`,
                suggested_one_liner: marketingHooks.oneLiner,
                keywords: marketingHooks.keywords,
                technical: {
                    bpm: profile.technical.bpm,
                    key: profile.technical.key
                }
            }, "Audio analyzed. Use this data to run `create_campaign_brief`.");

        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Unknown error';
            return toolError(`Failed to analyze audio for campaign: ${msg}`, "ANALYSIS_FAILED");
        }
    }),

    analyze_market_trends: wrapTool('analyze_market_trends', async ({ category }: { category?: string }) => {
        const electronWin = window as unknown as ElectronWindowAPI;
        if (!electronWin.electronAPI?.marketing) {
            return toolError("Marketing analysis bridge unavailable.", "IPC_ERROR");
        }

        try {
            const result = await electronWin.electronAPI.marketing.analyzeTrends({ category });
            if (!result.success) {
                return toolError(result.error || "Analysis failed", "SCRAPE_FAILED");
            }

            return toolSuccess(result.analysis, `Market analysis complete for ${category || 'pop'}.`);
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Unknown error';
            return toolError(`Failed to bridge to market analysis: ${msg}`, "BRIDGE_ERROR");
        }
    }),

    create_ab_test_campaign: wrapTool('create_ab_test_campaign', async ({ product, goal, channels }: { product: string; goal: string; channels: string[] }) => {
        const variants = [
            { id: 'varA', text: `Catchy headline for ${product} - Don't miss out!`, target_audience: 'Broad' },
            { id: 'varB', text: `The story behind ${product}. Hear it now.`, target_audience: 'Niche/Fans' },
            { id: 'varC', text: `Join the movement. Stream ${product} today.`, target_audience: 'Lookalike' }
        ];

        return toolSuccess({
            campaign_id: `camp-${crypto.randomUUID()}`,
            product,
            goal,
            channels,
            variants,
            pixel_framework: {
                status: 'draft_pixel_plan',
                events_tracked: ['ViewContent', 'AddToCart', 'Purchase'],
                requires_setup: ['provider_pixel_id', 'installed_tracking_code', 'verified_event_receipt', 'connected_ad_account']
            }
        }, `A/B testing campaign draft created for ${product} with 3 copy variants. Tracking pixel requires provider setup: need pixel ID, install code, verified events, and connected ad account.`);
    }),

    generate_ab_campaign: wrapTool('generate_ab_campaign', async (args: { productName: string; targetAudience: string; platform: 'Meta' | 'TikTok' | 'YouTube' }) => {
        const variants = [
            {
                id: 'angle-story',
                hook: `The story behind ${args.productName}`,
                copy: `For ${args.targetAudience}: hear the record before everyone else.`,
            },
            {
                id: 'angle-proof',
                hook: `${args.productName} is already moving`,
                copy: `Tap in now and help push the next independent wave.`,
            },
            {
                id: 'angle-direct',
                hook: `Play ${args.productName} today`,
                copy: `Built for ${args.targetAudience}. Stream, save, and share it.`,
            },
        ];

        return toolSuccess({
            campaignId: `ab-${Date.now().toString(36)}`,
            platform: args.platform,
            targetAudience: args.targetAudience,
            variants,
            trackingPixel: {
                event: 'ViewContent',
                status: 'configured_for_review',
            },
        }, `Generated ${variants.length} ${args.platform} A/B campaign variants for ${args.productName}.`);
    }),

    deploy_micro_ad_campaign: wrapTool('deploy_micro_ad_campaign', async (args: {
        platform: 'Meta' | 'TikTok';
        dailyBudgetUsd: number;
        durationDays: number;
        targetAudienceProfile: string;
        creativeVariants: string[];
    }) => {
        const totalBudget = Math.max(0, args.dailyBudgetUsd) * Math.max(1, args.durationDays);

        return toolSuccess({
            campaignId: `micro-${Date.now().toString(36)}`,
            platform: args.platform,
            dailyBudgetUsd: args.dailyBudgetUsd,
            durationDays: args.durationDays,
            totalBudgetUsd: totalBudget,
            targetAudienceProfile: args.targetAudienceProfile,
            creativeVariants: args.creativeVariants,
            status: 'ready_for_approval',
            note: 'Live ad platform credentials are not invoked from this tool. The campaign package is prepared for review/deployment.',
        }, `${args.platform} micro-campaign package prepared for ${args.durationDays} day(s) at $${args.dailyBudgetUsd}/day.`);
    }),

    deploy_email_newsletter: wrapTool('deploy_email_newsletter', async (args: {
        subjectLine: string;
        segmentName: string;
        htmlContent: string;
        sendAt?: string;
    }) => {
        return toolSuccess({
            campaignId: `email-${Date.now().toString(36)}`,
            subjectLine: args.subjectLine,
            segmentName: args.segmentName,
            htmlPreview: args.htmlContent.slice(0, 500),
            sendAt: args.sendAt || 'immediate',
            status: 'requires_email_provider_credentials',
            note: 'Provider handoff is prepared; external Mailchimp/Klaviyo send requires configured credentials.',
        }, `Newsletter "${args.subjectLine}" prepared for ${args.segmentName}; provider credentials are required before sending.`);
    }),

    generate_presave_campaign: wrapTool('generate_presave_campaign', async (args: {
        trackTitle: string;
        releaseDate: string;
        artworkUrl: string;
        collectPhoneNumbers?: boolean;
    }) => {
        const slug = args.trackTitle
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '') || 'release';

        return toolSuccess({
            campaignId: `presave-${Date.now().toString(36)}`,
            landingPageSlug: slug,
            headline: `Pre-save ${args.trackTitle}`,
            releaseDate: args.releaseDate,
            artworkUrl: args.artworkUrl,
            fields: ['email', ...(args.collectPhoneNumbers ? ['phone'] : [])],
            callsToAction: ['Spotify Pre-save', 'Apple Music Notify', 'YouTube Reminder'],
            status: 'draft_ready',
        }, `Pre-save campaign draft generated for ${args.trackTitle}.`);
    }),

    deploy_sms_blast: wrapTool('deploy_sms_blast', async (args: {
        messageBody: string;
        segmentName: string;
        mediaUrl?: string;
    }) => {
        return toolSuccess({
            blastId: `sms-${Date.now().toString(36)}`,
            segmentName: args.segmentName,
            characterCount: args.messageBody.length,
            mediaUrl: args.mediaUrl || null,
            status: 'requires_sms_provider_credentials',
            note: 'SMS content is queued for provider handoff; Twilio credentials are required for live delivery.',
        }, `SMS blast prepared for ${args.segmentName}; Twilio credentials are required before sending.`);
    }),

    enrich_fan_data: wrapTool('enrich_fan_data', async (args: { emailAddress: string }) => {
        const domain = args.emailAddress.includes('@') ? args.emailAddress.split('@')[1] : 'unknown';
        return toolSuccess({
            emailAddress: args.emailAddress,
            domain,
            enrichmentStatus: 'local_profile_only',
            inferredSegments: domain === 'unknown' ? ['needs_cleanup'] : ['email_contact', 'owned_audience'],
            note: 'No third-party enrichment API is called without configured provider credentials.',
        }, `Fan profile enriched locally for ${args.emailAddress}.`);
    }),

    generate_influencer_bounty: wrapTool('generate_influencer_bounty', async (args: {
        trackTitle: string;
        bountyRewardUsd: number;
        soundUrl: string;
        targetInfluencerNiche?: string;
    }) => {
        return toolSuccess({
            bountyId: `bounty-${Date.now().toString(36)}`,
            trackTitle: args.trackTitle,
            rewardUsd: args.bountyRewardUsd,
            soundUrl: args.soundUrl,
            targetInfluencerNiche: args.targetInfluencerNiche || 'music discovery creators',
            referralRules: [
                'Use the official sound URL',
                'Tag the artist account',
                'Submit post URL for review',
            ],
            status: 'draft_ready',
        }, `Influencer bounty draft generated for ${args.trackTitle}.`);
    }),

    // ISSUE-848: this used to silently swallow both "no user signed in" and
    // a real Firestore read failure into the same all-zero success result,
    // making them indistinguishable from a genuinely empty CRM.
    tier_superfans: wrapTool('tier_superfans', async (args: { minSpendForVIP: number; minSpendForSuperfan: number }) => {
        const { db, auth } = await importWithRetry(() => import('@/services/firebase'));
        const uid = auth.currentUser?.uid;
        if (!uid) {
            return toolError('No user is signed in. Fan CRM tiering requires an authenticated user.', 'AUTH_REQUIRED');
        }

        const { collection, getDocs } = await importWithRetry(() => import('firebase/firestore'));

        let purchasesSnap;
        try {
            purchasesSnap = await getDocs(collection(db, 'users', uid, 'fanPurchases'));
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            logger.warn('[MarketingTools] tier_superfans Firestore read failed:', err);
            return toolError(`Could not read fan purchase records: ${message}`, 'FAN_PURCHASES_UNAVAILABLE');
        }

        const results = { Standard: 0, VIP: 0, Superfan: 0 };
        const fanSpend: Record<string, number> = {};
        purchasesSnap.forEach(doc => {
            const data = doc.data();
            const fanId = data.fanId as string;
            if (fanId) {
                fanSpend[fanId] = (fanSpend[fanId] || 0) + (Number(data.amount) || 0);
            }
        });

        Object.values(fanSpend).forEach(spend => {
            if (spend >= args.minSpendForSuperfan) results.Superfan++;
            else if (spend >= args.minSpendForVIP) results.VIP++;
            else results.Standard++;
        });

        const total = results.Standard + results.VIP + results.Superfan;
        const message = total === 0
            ? 'Fan CRM tiered: no purchase records found for this account.'
            : `Fan CRM tiered (${total} fans): ${results.Superfan} Superfans, ${results.VIP} VIPs, ${results.Standard} Standard.`;

        return toolSuccess({
            tiers: results,
            thresholds: {
                vip: args.minSpendForVIP,
                superfan: args.minSpendForSuperfan,
            },
            totalFans: total,
            source: 'fanPurchases',
            recordsRead: purchasesSnap.size,
        }, message);
    }),

    track_post_release_momentum: wrapTool('track_post_release_momentum', async (args: { trackId: string; adSpend: number; organicStreams: number; dsp: string }) => {
        const roi = (args.organicStreams * 0.004) / (args.adSpend || 1);
        const momentumScore = Math.min(100, (args.organicStreams / 1000) * 5 + (roi * 10));

        return toolSuccess({
            trackId: args.trackId,
            dsp: args.dsp,
            adSpend: args.adSpend,
            organicStreams: args.organicStreams,
            momentumScore: Math.round(momentumScore),
            estimatedRoiMultiplier: Number(roi.toFixed(2)),
            trend: momentumScore > 50 ? 'Accelerating' : 'Decelerating'
        }, `Post-release momentum tracked for ${args.trackId} on ${args.dsp}. Momentum Score: ${Math.round(momentumScore)}/100. Trend: ${momentumScore > 50 ? 'Accelerating' : 'Decelerating'}.`);
    }),

    generate_ad_copy: wrapTool('generate_ad_copy', async (args: { productName: string; targetAudience: string; tone?: string; platform: string }) => {
        const tone = args.tone || 'engaging';
        return toolSuccess({
            copy: `Discover ${args.productName}. Tailored for ${args.targetAudience} with a ${tone} vibe on ${args.platform}.`,
            hashtags: ['#' + args.productName.replace(/\s+/g, ''), '#music', '#newrelease'],
            callToAction: 'Listen Now'
        }, `Ad copy generated for ${args.productName} on ${args.platform}.`);
    }),

    analyze_campaign_roi: wrapTool('analyze_campaign_roi', async (args: { campaignId: string; totalSpend: number; totalRevenue: number }) => {
        const roiPercentage = args.totalSpend > 0 ? ((args.totalRevenue - args.totalSpend) / args.totalSpend) * 100 : 0;
        return toolSuccess({
            campaignId: args.campaignId,
            roiPercentage: Number(roiPercentage.toFixed(2)),
            profitable: roiPercentage > 0,
            summary: `Campaign ${args.campaignId} yielded a ${roiPercentage.toFixed(2)}% ROI.`
        }, `ROI analyzed for campaign ${args.campaignId}.`);
    }),

    dispatch_multi_platform_campaign: wrapTool('dispatch_multi_platform_campaign', async (args: {
        directiveId?: string;
        ledgerAccountId: string;
        objective: 'DSP_STREAM_CONVERSION' | 'PRE_SAVE_INTAKE' | 'WAITLIST_INTAKE' | 'MERCH_CONVERSION' | 'EVENT_TICKETING';
        platforms: Array<'META' | 'TIKTOK' | 'GOOGLE_ADS'>;
        totalBudgetCents: number;
        dailyBudgetCents: number;
        currency?: string;
        platformSplitPercentages: Partial<Record<'META' | 'TIKTOK' | 'GOOGLE_ADS', number>>;
        releaseId: string;
        isrc: string;
        upc?: string;
        smartlinkId: string;
        smartlinkUrl: string;
        videoAssetIds: string[];
        imageAssetIds: string[];
        tiktokSparkAuthCodes?: string[];
        youtubeVideoIds?: string[];
        geoLocations: string[];
        genreTaxonomyTags: string[];
        similarArtistEntityIds?: string[];
        startTimeIso?: string;
        endTimeIso?: string;
    }) => {
        try {
            const { auth, functions } = await importWithRetry(() => import('@/services/firebase'));
            const uid = auth.currentUser?.uid;
            if (!uid) {
                return toolError(
                    'No user is signed in. Multi-platform campaign execution requires an authenticated artist.',
                    'AUTH_REQUIRED',
                );
            }

            const { httpsCallable } = await importWithRetry(() => import('firebase/functions'));
            const executeCallable = httpsCallable<
                Record<string, unknown>,
                { ok: boolean; result: Record<string, unknown> }
            >(functions, 'executeUnifiedCampaignDirective');

            const directivePayload = {
                directiveId: args.directiveId || `dir_${Date.now()}`,
                triggeredBy: 'MARKETING_AGENT' as const,
                artistId: uid,
                ledgerAccountId: args.ledgerAccountId,
                objective: args.objective,
                platforms: args.platforms,
                budgetAllocation: {
                    currency: (args.currency || 'USD').toUpperCase(),
                    totalBudgetCents: args.totalBudgetCents,
                    dailyBudgetCents: args.dailyBudgetCents,
                    platformSplitPercentages: args.platformSplitPercentages,
                },
                schedule: {
                    startTimeIso: args.startTimeIso || new Date().toISOString(),
                    ...(args.endTimeIso ? { endTimeIso: args.endTimeIso } : {}),
                },
                assets: {
                    releaseId: args.releaseId,
                    isrc: args.isrc,
                    ...(args.upc ? { upc: args.upc } : {}),
                    smartlinkId: args.smartlinkId,
                    smartlinkUrl: args.smartlinkUrl,
                    videoAssetIds: args.videoAssetIds,
                    imageAssetIds: args.imageAssetIds,
                    ...(args.tiktokSparkAuthCodes ? { tiktokSparkAuthCodes: args.tiktokSparkAuthCodes } : {}),
                    ...(args.youtubeVideoIds ? { youtubeVideoIds: args.youtubeVideoIds } : {}),
                },
                targeting: {
                    geoLocations: args.geoLocations,
                    genreTaxonomyTags: args.genreTaxonomyTags,
                    similarArtistEntityIds: args.similarArtistEntityIds || [],
                },
            };

            const response = await executeCallable(directivePayload);
            const result = response.data.result;
            const receipts = Array.isArray(result.receipts) ? result.receipts : [];
            const committedCents = typeof result.totalCommittedBudgetCents === 'number'
                ? result.totalCommittedBudgetCents
                : 0;

            return toolSuccess(
                result,
                `Multi-platform campaign directive ${directivePayload.directiveId} executed across ${receipts.length} platform(s) (${args.platforms.join(', ')}). Committed ${committedCents} cents from recoupment ledger ${args.ledgerAccountId}.`,
            );
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : String(error);
            logger.warn('[MarketingTools] dispatch_multi_platform_campaign failed:', error);
            return toolError(
                `Multi-platform campaign execution did not complete: ${msg}`,
                'CAMPAIGN_EXECUTION_FAILED',
            );
        }
    }),

    record_attribution_conversion: wrapTool('record_attribution_conversion', async (args: {
        eventId?: string;
        eventName: 'SmartlinkView' | 'DSPOutboundClick' | 'PreSaveComplete' | 'WaitlistSignup' | 'MerchPurchase';
        releaseId: string;
        isrc: string;
        smartlinkId: string;
        targetDsp?: 'SPOTIFY' | 'APPLE_MUSIC' | 'TIDAL' | 'AMAZON_MUSIC' | 'YOUTUBE_MUSIC' | 'BANDCAMP';
        fbclid?: string;
        ttclid?: string;
        gclid?: string;
        clientIpAddress: string;
        clientUserAgent: string;
        emailSha256?: string;
        phoneSha256?: string;
        currency?: string;
        valueCents?: number;
    }) => {
        try {
            const { auth, functions } = await importWithRetry(() => import('@/services/firebase'));
            const uid = auth.currentUser?.uid;
            if (!uid) {
                return toolError(
                    'No user is signed in. Server-side attribution dispatch requires an authenticated artist.',
                    'AUTH_REQUIRED',
                );
            }

            const { httpsCallable } = await importWithRetry(() => import('firebase/functions'));
            const ingestCallable = httpsCallable<
                Record<string, unknown>,
                { ok: boolean; receipt: Record<string, unknown> }
            >(functions, 'ingestUnifiedAttributionEvent');

            const eventPayload = {
                eventId: args.eventId || `evt_${Date.now()}`,
                eventTimestampUnix: Math.floor(Date.now() / 1000),
                eventName: args.eventName,
                releaseId: args.releaseId,
                isrc: args.isrc,
                smartlinkId: args.smartlinkId,
                ...(args.targetDsp ? { targetDsp: args.targetDsp } : {}),
                clickIdentifiers: {
                    ...(args.fbclid ? { fbclid: args.fbclid } : {}),
                    ...(args.ttclid ? { ttclid: args.ttclid } : {}),
                    ...(args.gclid ? { gclid: args.gclid } : {}),
                },
                userData: {
                    clientIpAddress: args.clientIpAddress,
                    clientUserAgent: args.clientUserAgent,
                    ...(args.emailSha256 ? { emailSha256: args.emailSha256 } : {}),
                    ...(args.phoneSha256 ? { phoneSha256: args.phoneSha256 } : {}),
                },
                ...(typeof args.valueCents === 'number'
                    ? {
                        valueMetadata: {
                            currency: (args.currency || 'USD').toUpperCase(),
                            valueCents: args.valueCents,
                        },
                    }
                    : {}),
            };

            const response = await ingestCallable(eventPayload);
            const receipt = response.data.receipt;

            return toolSuccess(
                receipt,
                `Attribution event ${eventPayload.eventId} (${args.eventName}) dispatched through server-side AttributionBus.`,
            );
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : String(error);
            logger.warn('[MarketingTools] record_attribution_conversion failed:', error);
            return toolError(
                `Attribution conversion dispatch did not complete: ${msg}`,
                'ATTRIBUTION_DISPATCH_FAILED',
            );
        }
    })
} satisfies Record<string, AnyToolFunction>;

// Aliases
export const {
    create_campaign_brief,
    analyze_audience,
    schedule_content,
    track_performance,
    generate_campaign_from_audio,
    analyze_market_trends,
    create_ab_test_campaign,
    tier_superfans,
    track_post_release_momentum,
    generate_ad_copy,
    analyze_campaign_roi,
    dispatch_multi_platform_campaign,
    record_attribution_conversion
} = MarketingTools;
